import { isSelectedRevenueDriver } from '../types/driverTerms';
/**
 * Budget pack export — the multi-tab Excel workbook offered on Budget
 * versions of the Forecast V3 workspace, typeset like the financial report's
 * P&L export (financialStatementExcel.js: statement row styles, accounting
 * number formats, Excel outline levels).
 *
 * Reader-first layout (hotel exec / owner):
 *   - "Overview" tab: KPI block (Occupied/Available Rooms, Occ %, ADR,
 *     RevPAR, Total Revenue/Expenses, Net Income by month) and a department
 *     directory whose rows HYPERLINK to each department's tab.
 *   - "P&L" tab: full statement — Statistics band, then Revenue and Expense
 *     departments with account detail COLLAPSED behind each department's
 *     outline group, so the summary reads at department grain and expands
 *     from Excel's outline controls.
 *   - One tab per department: summary (plus Occ/ADR/RevPAR on the rooms
 *     department), accounts by month, and a Driver Detail section that leads
 *     with the accounts a driver actually moves — rates per month, Labor
 *     Config positions, Zero Base layers, % bases — while manual/static
 *     entries collapse into a single group at the end.
 *
 * Accounts with no budgeted amount in any month are omitted (the count is
 * noted in each tab's header) — a zero row tells an owner nothing.
 */

import * as XLSX from 'xlsx';
import type { ForecastAccount, LaborPositionConfig, ZeroBaseDriverLayer } from '../types';
import { MONTHS } from '../types';
import { driverLabel } from '../types/driverTerms';
import { buildDriverSubRows } from '../components/workspace/DriverOverrideRows';
import type { DriverSubRow, RateUnit } from '../components/workspace/DriverOverrideRows';
// The expanded-view column registry: the pack's comparison columns are the
// same definitions the grid renders, computed over summed expanded values.
import { getActiveColumns } from '../components/workspace/columnRegistry';
import type { ExpandedColumnDef, ColumnRenderContext } from '../components/workspace/columnRegistry';
import type { BuildForecastRawExportInput, RawExportDepartment } from './forecastRawExport';
import { resolveStatAccountMonths } from './forecastRawExport';
import type { StatLeaf } from './forecastRawExport';
import { buildFormattedExportSheet } from './exportFormats';
import type { BudgetExportFormat } from './exportFormats';
// Statement writer shared with the financial report exports (plain JS module).
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — untyped JS module
import { buildStatementSheet, downloadWorkbookXlsx } from '../../../utils/financialStatementExcel';
// USALI classification shared with the financial report's GOP view — same
// section constants, name regex and override precedence (financialPnlMatrix
// buildUsaliUnits), so the pack's GOP line ties to the report's.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — untyped JS module
import { USALI_SECTIONS, USALI_PRECEDENCE, BELOW_GOP_SECTIONS, NON_OPERATING_NAME } from '../../../utils/financialPnlMatrix';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

type CellValue = number | string;
type Cells = Record<string, CellValue>;

export type StatementStyle = 'title' | 'section' | 'group' | 'detail' | 'groupTotal' | 'sectionTotal' | 'net' | 'blank';

export interface StatementRow {
  style: StatementStyle;
  label: string;
  indent: number;
  cells: Cells | null;
  format?: string;
  /** Starts collapsed behind its outline group in Excel. */
  hidden?: boolean;
  /** Excel outline level when it should differ from the label indent. */
  outlineLevel?: number;
  /** In-workbook hyperlink target, e.g. "#'Rooms'!A1". */
  link?: string;
}

export interface StatementColumn {
  key: string;
  label: string;
  format?: string;
  /** Merged header line above the metric labels, e.g. "Jan" over Budget · Actual · LY. */
  group?: string;
  /** Excel column outline level — comparison columns collapse behind the plan column. */
  level?: number;
  /** Starts collapsed behind its column outline group. */
  hidden?: boolean;
}

export interface BudgetPackSheetSpec {
  sheetName: string;
  title: string;
  subtitle?: string;
  metaLines?: string[];
  rowHeaderLabel?: string;
  columns: StatementColumn[];
  rows: StatementRow[];
}

export interface DeptUsaliRow {
  id?: number | null;
  name?: string | null;
  usaliSection?: string | null;
}

export interface AccountUsaliRow {
  id: number;
  usaliSection?: string | null;
}

/**
 * Comparison columns for the pack — the workspace's expanded-view columns
 * (Actual, LY, Act vs Bud, …) carried into the workbook as collapsed column
 * groups behind each month's plan column, plus the annual total.
 */
export interface PackComparisonInput {
  isBudget?: boolean;
  /** Registry keys of the user's visible expanded columns, in display order. */
  columnKeys: string[];
  /** Per-month occupied rooms (actual/forecast) — context for the $POR columns. */
  occupiedRoomsPerMonth?: Record<string, { actual?: number; forecast?: number }>;
  /** Per-month total-revenue expanded sums — context for the % Rev columns. */
  revenueExpandedTotals?: Record<string, { actual?: number; budget?: number; forecast?: number; lyBudget?: number }>;
}

/** A chart-of-accounts row, for the Export tab's zero-fill of accounts the plan doesn't carry. */
export interface ExportChartAccount {
  id: number;
  accountNumber?: string | null;
  accountName?: string | null;
  accountType?: string | null;
  department?: string | null;
}

export interface BuildBudgetPackInput extends BuildForecastRawExportInput {
  versionName?: string;
  currency?: string | null;
  /** The property's full chart — every P&L account not in the plan still gets an Export row of zeros. */
  chartOfAccounts?: ExportChartAccount[];
  /** chartOfAccountDepartments rows — department-default USALI sections. */
  deptUsaliSections?: DeptUsaliRow[];
  /** ChartOfAccount rows — account-level USALI sections (override the department default, as the report's COALESCE does). */
  accountUsaliSections?: AccountUsaliRow[];
  /** When set, months (and the total) carry the expanded-view comparison columns as collapsed groups. */
  comparison?: PackComparisonInput | null;
}

const round2 = (v: number) => Math.round(v * 100) / 100;
const sumMonths = (cells: (number | null)[]): number | null => {
  const present = cells.filter((v): v is number => v !== null && Number.isFinite(v));
  return present.length ? present.reduce((a, b) => a + b, 0) : null;
};

/** Title-case that leaves short all-caps tokens (IDA, A&G, POM) alone. */
function titleCase(s: string): string {
  return String(s || '')
    .split(/\s+/)
    .map((word) => {
      if (word.length <= 3 && /[A-Z]/.test(word) && word === word.toUpperCase()) return word;
      const lower = word.toLowerCase();
      if (lower === 'and') return 'And';
      return lower.replace(/^([a-z])/, (m) => m.toUpperCase());
    })
    .join(' ');
}

const normalizeDept = (s: string) => String(s || '').trim().replace(/\s+/g, ' ').toLowerCase();

/** Excel sheet names: ≤31 chars, no []:*?/\ and unique within the workbook. */
export function sanitizeSheetName(raw: string, taken: Set<string>): string {
  let base = String(raw || 'Sheet').replace(/[[\]:*?/\\]/g, ' ').replace(/\s+/g, ' ').trim() || 'Sheet';
  // " & " reads as well as " And " and rescues names from the 31-char cap
  // ("Property Operation & Maintenance" fits; the spelled-out form doesn't).
  if (base.length > 31) base = base.replace(/ And /g, ' & ');
  base = base.slice(0, 31).trim();
  let name = base;
  let n = 2;
  while (taken.has(name.toLowerCase())) {
    const suffix = ` (${n})`;
    name = base.slice(0, 31 - suffix.length) + suffix;
    n += 1;
  }
  taken.add(name.toLowerCase());
  return name;
}

const sheetLink = (sheetName: string) => `#'${sheetName.replace(/'/g, "''")}'!A1`;

/** Month cells (jan..dec) + total from a record of month values. */
function moneyCells(rec: Record<string, number>): Cells {
  const cells: Cells = {};
  let total = 0;
  MONTHS.forEach((m) => {
    const v = round2(Number(rec?.[m] ?? 0));
    cells[m] = v;
    total += v;
  });
  cells.total = round2(total);
  return cells;
}

function leafCells(months: (number | null)[], total?: number | null): Cells {
  const cells: Cells = {};
  months.forEach((v, i) => {
    if (v !== null && Number.isFinite(v)) cells[MONTHS[i]] = round2(v);
  });
  const t = total === undefined ? sumMonths(months) : total;
  if (t !== null && t !== undefined && Number.isFinite(t)) cells.total = round2(t);
  return cells;
}

/** month-by-month a/b with the total recomputed from the summed parts. */
function ratioCells(numer: (number | null)[], denom: (number | null)[], scale = 1): Cells {
  const cells: Cells = {};
  MONTHS.forEach((m, i) => {
    const n = numer[i];
    const d = denom[i];
    if (n !== null && d !== null && Number.isFinite(n) && Number.isFinite(d) && d > 0) cells[m] = round2((n / d) * scale);
  });
  const nt = sumMonths(numer);
  const dt = sumMonths(denom);
  if (nt !== null && dt !== null && dt > 0) cells.total = round2((nt / dt) * scale);
  return cells;
}

// ─── Comparison columns (expanded-view columns as collapsed Excel groups) ───

/** Ratio columns recompute from summed context on single-type scopes and stay blank on mixed (GOP/NET) rows. */
const RATIO_COLUMN_KEYS = new Set(['por', 'fcstPor', 'budPor', 'porVar', 'pctRev', 'budPctRev', 'fcstPctRev', 'lyBudgetPor', 'lyBudgetPctRev']);

/** Excel number format per registry column. */
const comparisonFormat = (def: ExpandedColumnDef): string => {
  if (def.key.toLowerCase().includes('pctrev') || def.key === 'pctRev') return 'percent1';
  if (RATIO_COLUMN_KEYS.has(def.key)) return 'currency2';
  return 'currency';
};

/** The comparison defs to export: the user's visible columns minus the plan itself. */
export function comparisonDefs(comparison?: PackComparisonInput | null): ExpandedColumnDef[] {
  if (!comparison?.columnKeys?.length) return [];
  return getActiveColumns(comparison.columnKeys, comparison.isBudget).filter((d) => d.key !== 'forecast');
}

interface ExpandedSum {
  forecast: number;
  budget: number;
  actual: number;
  lyActual: number;
  lyBudget: number;
  lyBudgetOccupiedRooms: number;
  plan: number;
}

const zeroSum = (): ExpandedSum => ({ forecast: 0, budget: 0, actual: 0, lyActual: 0, lyBudget: 0, lyBudgetOccupiedRooms: 0, plan: 0 });

/** Raw expanded values summed per month (plus the year) across accounts of ONE type. */
function sumExpanded(accounts: ForecastAccount[]): Record<string, ExpandedSum> {
  const out: Record<string, ExpandedSum> = { total: zeroSum() };
  MONTHS.forEach((m) => {
    out[m] = zeroSum();
  });
  accounts.forEach((a) => {
    MONTHS.forEach((m) => {
      const t = out[m];
      const plan = Number(a.monthlyValues?.[m] ?? 0);
      t.plan += plan;
      const e = a.monthlyExpanded?.[m];
      if (e) {
        t.forecast += Number(e.forecast ?? 0);
        t.budget += Number(e.budget ?? 0);
        t.actual += Number(e.actual ?? 0);
        t.lyActual += Number(e.lyActual ?? 0);
        t.lyBudget += Number(e.lyBudget ?? 0);
        t.lyBudgetOccupiedRooms = Number(e.lyBudgetOccupiedRooms ?? t.lyBudgetOccupiedRooms);
      }
    });
  });
  MONTHS.forEach((m) => {
    const t = out[m];
    const y = out.total;
    y.forecast += t.forecast;
    y.budget += t.budget;
    y.actual += t.actual;
    y.lyActual += t.lyActual;
    y.lyBudget += t.lyBudget;
    y.lyBudgetOccupiedRooms += t.lyBudgetOccupiedRooms;
    y.plan += t.plan;
  });
  return out;
}

function comparisonCtx(
  sums: ExpandedSum,
  key: string,
  monthIndex: number,
  isExpense: boolean,
  comparison: PackComparisonInput
): ColumnRenderContext {
  // The annual column sums the monthly context the same way the sums were built.
  const monthsFor = key === 'total' ? MONTHS : [key];
  let actualRooms = 0;
  let forecastRooms = 0;
  let actualRev = 0;
  let budgetRev = 0;
  let lyBudgetRev = 0;
  let forecastRev = 0;
  monthsFor.forEach((m) => {
    const rooms = comparison.occupiedRoomsPerMonth?.[m];
    actualRooms += Number(rooms?.actual ?? 0);
    forecastRooms += Number(rooms?.forecast ?? 0);
    const rev = comparison.revenueExpandedTotals?.[m];
    actualRev += Number(rev?.actual ?? 0);
    budgetRev += Number(rev?.budget ?? 0);
    lyBudgetRev += Number(rev?.lyBudget ?? 0);
    forecastRev += Number(rev?.forecast ?? 0);
  });
  return {
    expanded: { forecast: sums.forecast, budget: sums.budget, actual: sums.actual, lyActual: sums.lyActual, lyBudget: sums.lyBudget, lyBudgetOccupiedRooms: sums.lyBudgetOccupiedRooms },
    value: sums.plan,
    isExpense,
    monthIndex,
    actualOccupiedRooms: actualRooms || undefined,
    forecastOccupiedRooms: forecastRooms || undefined,
    // Same normalization the grid applies: expanded revenue actuals are stored negative.
    actualTotalRevenue: actualRev ? actualRev * -1 : 0,
    budgetTotalRevenue: budgetRev || 0,
    lyBudgetTotalRevenue: lyBudgetRev,
    forecastTotalRevenue: forecastRev || 0,
  };
}

/**
 * Comparison cells for a scope: `{jan:actual: …, total:ly: …}` keyed
 * `<month>:<columnKey>`. Single-type scopes (an account, a department side, a
 * section) run every column over the summed context; mixed scopes (GOP, NET)
 * compose the sides — data columns subtract expenses, variance columns add
 * (their expense sign is already favorable-positive) — and skip ratio columns.
 */
function comparisonCells(
  revenueAccounts: ForecastAccount[],
  expenseAccounts: ForecastAccount[],
  comparison: PackComparisonInput | null | undefined,
  defs: ExpandedColumnDef[]
): Cells {
  const cells: Cells = {};
  if (!comparison || defs.length === 0) return cells;
  const rev = revenueAccounts.length ? sumExpanded(revenueAccounts) : null;
  const exp = expenseAccounts.length ? sumExpanded(expenseAccounts) : null;
  if (!rev && !exp) return cells;
  const mixed = Boolean(rev && exp);
  const keys = [...MONTHS.map((m, i) => ({ key: m, monthIndex: i })), { key: 'total', monthIndex: 11 }];
  keys.forEach(({ key, monthIndex }) => {
    defs.forEach((def) => {
      let v: number;
      if (mixed) {
        if (RATIO_COLUMN_KEYS.has(def.key)) return;
        const rv = def.getValue(comparisonCtx(rev![key], key, monthIndex, false, comparison));
        const ev = def.getValue(comparisonCtx(exp![key], key, monthIndex, true, comparison));
        v = def.colorMode === 'variance' ? rv + ev : rv - ev;
      } else {
        const side = (rev ?? exp)!;
        v = def.getValue(comparisonCtx(side[key], key, monthIndex, !rev, comparison));
      }
      if (Number.isFinite(v)) cells[`${key}:${def.key}`] = round2(v);
    });
  });
  return cells;
}

function statementColumns(
  year: number,
  actualMonthKeys: Set<string>,
  planLabel: string,
  comparison?: PackComparisonInput | null
): StatementColumn[] {
  const defs = comparisonDefs(comparison);
  if (defs.length === 0) {
    return [
      ...MONTHS.map((m, i) => ({
        key: m,
        label: `${MONTH_NAMES[i]}${actualMonthKeys.has(m) ? ' Act' : ''}`,
        format: 'currency',
      })),
      { key: 'total', label: `${year} ${planLabel}`, format: 'currency' },
    ];
  }
  // Grouped layout: the month (or "<year> Total") rides the merged header
  // line; beneath it the plan column stays visible and the comparison columns
  // start collapsed behind an Excel column outline.
  const columns: StatementColumn[] = [];
  const pushGroup = (key: string, group: string) => {
    columns.push({ key, label: planLabel, format: 'currency', group });
    defs.forEach((def) => {
      columns.push({
        key: `${key}:${def.key}`,
        label: def.label,
        format: comparisonFormat(def),
        group,
        level: 1,
        hidden: true,
      });
    });
  };
  MONTHS.forEach((m, i) => pushGroup(m, `${MONTH_NAMES[i]}${actualMonthKeys.has(m) ? ' Act' : ''}`));
  pushGroup('total', `${year} Total`);
  return columns;
}

const accountLabel = (a: ForecastAccount) => `${a.accountNumber} · ${a.accountName}`.replace(/^ · /, '');
const accountHasValues = (a: ForecastAccount) => MONTHS.some((m) => Number(a.monthlyValues?.[m] ?? 0) !== 0);
const leafHasValues = (leaf: StatLeaf) => leaf.months.some((v) => v !== null && v !== 0);

const blankRow = (rows: StatementRow[]) => {
  if (rows.length > 0 && rows[rows.length - 1].style !== 'blank') rows.push({ style: 'blank', label: '', indent: 0, cells: null });
};

// ─── Zero-account suppression ───

interface FilteredPack {
  input: BuildBudgetPackInput;
  /** zero-amount accounts dropped, per normalized department name and overall */
  omittedByDept: Map<string, number>;
  omittedTotal: number;
}

function suppressZeroAccounts(input: BuildBudgetPackInput): FilteredPack {
  const omittedByDept = new Map<string, number>();
  let omittedTotal = 0;
  const filterDepts = (depts: RawExportDepartment[]): RawExportDepartment[] =>
    depts
      .map((d) => {
        const kept = d.accounts.filter(accountHasValues);
        const dropped = d.accounts.length - kept.length;
        if (dropped > 0) {
          const key = normalizeDept(d.department);
          omittedByDept.set(key, (omittedByDept.get(key) || 0) + dropped);
          omittedTotal += dropped;
        }
        return { ...d, accounts: kept };
      })
      .filter((d) => d.accounts.length > 0);
  return {
    input: {
      ...input,
      revenueDepartments: filterDepts(input.revenueDepartments),
      expenseDepartments: filterDepts(input.expenseDepartments),
    },
    omittedByDept,
    omittedTotal,
  };
}

const omittedNote = (n: number) => (n > 0 ? `${n} account${n === 1 ? '' : 's'} with no budgeted amounts omitted` : '');

// ─── Statistics ───

interface PackStats {
  occupied: (number | null)[];
  available: (number | null)[];
  occPct: Cells;
  hasRooms: boolean;
  resolved: ReturnType<typeof resolveStatAccountMonths> | null;
  /** Revenue chartOfAccountIds linked from Occupied-Rooms stat accounts. */
  linkedRevenueIds: Set<number>;
  /** Rooms revenue per month — the linked revenue accounts summed, falling
   *  back to a department named "Rooms" when nothing is linked. */
  roomsRevenue: (number | null)[] | null;
}

function computeStats(input: BuildBudgetPackInput): PackStats {
  const empty: PackStats = {
    occupied: MONTHS.map(() => null),
    available: MONTHS.map(() => null),
    occPct: {},
    hasRooms: false,
    resolved: null,
    linkedRevenueIds: new Set(),
    roomsRevenue: null,
  };
  const hasStats = !!input.roomStats || (input.statAccounts?.length ?? 0) > 0;
  if (!hasStats) return empty;
  const resolved = resolveStatAccountMonths(input);
  const occPct = ratioCells(resolved.occupied, resolved.available, 100);
  const hasRooms = resolved.occupied.some((v) => v !== null) || resolved.available.some((v) => v !== null);

  const linkedRevenueIds = new Set<number>();
  resolved.occupiedLeaves.forEach((leaf) => {
    if (leaf.account.revenueChartOfAccountId != null) linkedRevenueIds.add(leaf.account.revenueChartOfAccountId);
  });
  // ADR/RevPAR numerator: the room-revenue accounts the stat segments link to
  // — department names ("Rooms Revenue", "Rooms") vary too much to key on.
  let roomsRevenue: (number | null)[] | null = null;
  const linkedAccounts = input.revenueDepartments.flatMap((d) => d.accounts.filter((a) => linkedRevenueIds.has(a.chartOfAccountId)));
  if (linkedAccounts.length > 0) {
    roomsRevenue = MONTHS.map((m) => round2(linkedAccounts.reduce((sum, a) => sum + Number(a.monthlyValues?.[m] ?? 0), 0)));
  } else {
    const roomsDept = input.revenueDepartments.find((d) => normalizeDept(d.department) === 'rooms');
    if (roomsDept) roomsRevenue = MONTHS.map((m) => round2(Number(roomsDept.monthlyTotals?.[m] ?? 0)));
  }
  return { occupied: resolved.occupied, available: resolved.available, occPct, hasRooms, resolved, linkedRevenueIds, roomsRevenue };
}

/** Occ % / ADR / RevPAR rows (ADR + RevPAR only when rooms revenue is resolvable). */
function kpiRows(stats: PackStats, indent: number): StatementRow[] {
  if (!stats.hasRooms) return [];
  const rows: StatementRow[] = [];
  rows.push({ style: 'group', label: 'Occupied Rooms', indent, cells: leafCells(stats.occupied), format: 'number' });
  rows.push({ style: 'group', label: 'Available Rooms', indent, cells: leafCells(stats.available), format: 'number' });
  if (Object.keys(stats.occPct).length) rows.push({ style: 'group', label: 'Occupancy %', indent, cells: stats.occPct, format: 'percent1' });
  if (stats.roomsRevenue) {
    const adr = ratioCells(stats.roomsRevenue, stats.occupied);
    const revpar = ratioCells(stats.roomsRevenue, stats.available);
    if (Object.keys(adr).length) rows.push({ style: 'group', label: 'ADR', indent, cells: adr, format: 'currency2' });
    if (Object.keys(revpar).length) rows.push({ style: 'group', label: 'RevPAR', indent, cells: revpar, format: 'currency2' });
  }
  return rows;
}

// ─── USALI / GOP classification (mirrors financialPnlMatrix buildUsaliUnits) ───

interface GopBands {
  /** Monthly Gross Operating Profit + total (display-positive dollars). */
  gop: Cells;
  /** GOP ÷ operating revenue, percent units. */
  gopMargin: Cells;
  /** Expense departments inside GOP: operated first, then undistributed. */
  operatingExpenseDepts: RawExportDepartment[];
  /** Expense departments below the GOP line, in statement band order. */
  belowGopExpenseDepts: RawExportDepartment[];
}

function classifyGop(input: BuildBudgetPackInput): GopBands {
  const validSection = (raw: string | null | undefined): string | undefined => {
    const section = String(raw || '').toUpperCase();
    return (USALI_PRECEDENCE as string[]).includes(section) ? section : undefined;
  };
  const acctSectionById = new Map<number, string>();
  (input.accountUsaliSections || []).forEach((row) => {
    const section = validSection(row.usaliSection);
    if (section && row.id != null) acctSectionById.set(Number(row.id), section);
  });
  const deptSectionById = new Map<number, string>();
  const deptSectionByName = new Map<string, string>();
  (input.deptUsaliSections || []).forEach((row) => {
    const section = validSection(row.usaliSection);
    if (!section) return;
    if (row.id != null) deptSectionById.set(Number(row.id), section);
    const key = normalizeDept(row.name || '');
    if (key && !deptSectionByName.has(key)) deptSectionByName.set(key, section);
  });
  const revenueKeys = new Set(input.revenueDepartments.map((d) => normalizeDept(d.department)));

  // Same resolution as the report's unit classifier (buildUsaliUnits): each
  // member account resolves COALESCE(account.usaliSection, dept-by-id,
  // dept-by-name); the unit takes the collected overrides by
  // USALI_PRECEDENCE, else non-operating name → has revenue → Operated →
  // else Undistributed. Forecast accounts carry only Revenue/Expense types,
  // so the all-Other-Income/Expense inference from the report never fires.
  const sectionOf = (dept: RawExportDepartment): string => {
    const overrides = new Set<string>();
    const deptByName = deptSectionByName.get(normalizeDept(dept.department));
    dept.accounts.forEach((a) => {
      const s =
        acctSectionById.get(a.chartOfAccountId) ??
        (a.departmentId != null ? deptSectionById.get(a.departmentId) : undefined) ??
        deptByName;
      if (s) overrides.add(s);
    });
    const explicit = (USALI_PRECEDENCE as string[]).find((s) => overrides.has(s));
    if (explicit) return explicit;
    if (NON_OPERATING_NAME.test(dept.department || '')) return USALI_SECTIONS.NON_OPERATING;
    return revenueKeys.has(normalizeDept(dept.department)) ? USALI_SECTIONS.OPERATED : USALI_SECTIONS.UNDISTRIBUTED;
  };

  const belowGop = new Set(BELOW_GOP_SECTIONS as string[]);
  const inGop = (dept: RawExportDepartment) => !belowGop.has(sectionOf(dept));

  const gopRevenueDepts = input.revenueDepartments.filter(inGop);
  const operated = input.expenseDepartments.filter((d) => inGop(d) && sectionOf(d) === USALI_SECTIONS.OPERATED);
  const undistributed = input.expenseDepartments.filter((d) => inGop(d) && sectionOf(d) !== USALI_SECTIONS.OPERATED);
  const operatingExpenseDepts = [...operated, ...undistributed];
  const belowGopExpenseDepts = (BELOW_GOP_SECTIONS as string[]).flatMap((band) =>
    input.expenseDepartments.filter((d) => sectionOf(d) === band)
  );

  const monthSum = (depts: RawExportDepartment[], m: string) => depts.reduce((sum, d) => sum + Number(d.monthlyTotals?.[m] ?? 0), 0);
  const gop: Cells = {};
  const gopMargin: Cells = {};
  let revTotal = 0;
  let gopTotal = 0;
  MONTHS.forEach((m) => {
    const rev = monthSum(gopRevenueDepts, m);
    const value = round2(rev - monthSum(operatingExpenseDepts, m));
    gop[m] = value;
    revTotal += rev;
    gopTotal += value;
    if (rev > 0) gopMargin[m] = round2((value / rev) * 100);
  });
  gop.total = round2(gopTotal);
  if (revTotal > 0) gopMargin.total = round2((gopTotal / revTotal) * 100);
  return { gop, gopMargin, operatingExpenseDepts, belowGopExpenseDepts };
}

// ─── Overview tab ───

function buildOverviewSpec(
  input: BuildBudgetPackInput,
  stats: PackStats,
  gop: GopBands,
  departments: { dept: DeptSections; sheetName: string }[],
  pnlSheetName: string,
  omittedTotal: number
): BudgetPackSheetSpec {
  const { locationName, year, actualMonthKeys, planLabel = 'Budget' } = input;
  const rows: StatementRow[] = [];

  rows.push({ style: 'section', label: 'KEY METRICS', indent: 0, cells: null });
  rows.push(...kpiRows(stats, 1));
  rows.push({ style: 'groupTotal', label: 'Total Revenue', indent: 1, cells: moneyCells(input.revenueTotals) });
  rows.push({ style: 'groupTotal', label: 'Gross Operating Profit (GOP)', indent: 1, cells: gop.gop });
  if (Object.keys(gop.gopMargin).length) rows.push({ style: 'group', label: 'GOP Margin %', indent: 1, cells: gop.gopMargin, format: 'percent1' });
  rows.push({ style: 'groupTotal', label: 'Total Expenses', indent: 1, cells: moneyCells(input.expenseTotals) });
  rows.push({ style: 'net', label: 'NET INCOME', indent: 0, cells: moneyCells(input.netIncome) });
  blankRow(rows);

  // Department directory — each row jumps to that department's tab.
  const deptRow = (label: string, cells: Cells | null, sheetName: string): StatementRow => ({
    style: 'detail',
    label,
    indent: 1,
    cells,
    link: sheetLink(sheetName),
  });
  rows.push({ style: 'section', label: 'REVENUE BY DEPARTMENT', indent: 0, cells: null });
  departments.forEach(({ dept, sheetName }) => {
    if (dept.revenue) rows.push(deptRow(titleCase(dept.name), moneyCells(dept.revenue.monthlyTotals), sheetName));
  });
  rows.push({ style: 'sectionTotal', label: 'Total Revenue', indent: 0, cells: moneyCells(input.revenueTotals) });
  blankRow(rows);
  rows.push({ style: 'section', label: 'EXPENSES BY DEPARTMENT', indent: 0, cells: null });
  departments.forEach(({ dept, sheetName }) => {
    if (dept.expense) rows.push(deptRow(titleCase(dept.name), moneyCells(dept.expense.monthlyTotals), sheetName));
  });
  rows.push({ style: 'sectionTotal', label: 'Total Expenses', indent: 0, cells: moneyCells(input.expenseTotals) });
  blankRow(rows);
  rows.push({ style: 'detail', label: `Full statement: see the ${pnlSheetName} tab · department names link to their tabs`, indent: 0, cells: null });

  return {
    sheetName: 'Overview',
    title: locationName,
    subtitle: `${year} ${planLabel} · Overview`,
    metaLines: [input.versionName ? `Version: ${input.versionName}` : '', omittedNote(omittedTotal)],
    rowHeaderLabel: '',
    columns: statementColumns(year, actualMonthKeys, planLabel),
    rows,
  };
}

// ─── P&L tab ───

function statBandRows(stats: PackStats): StatementRow[] {
  const resolved = stats.resolved;
  if (!resolved) return [];
  const rows: StatementRow[] = [];
  const sortLeaves = (leaves: StatLeaf[]) =>
    [...leaves].sort(
      (a, b) =>
        a.account.accountNumber.localeCompare(b.account.accountNumber, undefined, { numeric: true }) ||
        a.account.accountName.localeCompare(b.account.accountName)
    );
  const leafRow = (leaf: StatLeaf): StatementRow => ({
    style: 'detail',
    label: `${leaf.account.accountNumber} · ${leaf.account.accountName}`.replace(/^ · /, ''),
    indent: 2,
    cells: leafCells(leaf.months),
    format: 'number',
    hidden: true,
  });
  const pushLeaves = (leaves: StatLeaf[]) => sortLeaves(leaves.filter(leafHasValues)).forEach((leaf) => rows.push(leafRow(leaf)));

  rows.push({ style: 'section', label: 'STATISTICS', indent: 0, cells: null });
  rows.push({ style: 'group', label: 'Occupied Rooms', indent: 1, cells: leafCells(resolved.occupied), format: 'number' });
  pushLeaves(resolved.occupiedLeaves);
  rows.push({ style: 'group', label: 'Available Rooms', indent: 1, cells: leafCells(resolved.available), format: 'number' });
  pushLeaves(resolved.availableLeaves);
  if (Object.keys(stats.occPct).length) rows.push({ style: 'group', label: 'Occupancy %', indent: 1, cells: stats.occPct, format: 'percent1' });

  Array.from(resolved.otherMapped.keys()).sort((a, b) => a.localeCompare(b)).forEach((type) => {
    const leaves = resolved.otherMapped.get(type)!.filter(leafHasValues);
    if (leaves.length === 0) return;
    const totals = MONTHS.map((_, i) => sumMonths(leaves.map((l) => l.months[i])));
    rows.push({ style: 'group', label: titleCase(type), indent: 1, cells: leafCells(totals), format: 'number' });
    pushLeaves(leaves);
  });
  const unmapped = resolved.unmapped.filter(leafHasValues);
  if (unmapped.length > 0) {
    rows.push({ style: 'group', label: 'Other Statistics', indent: 1, cells: null });
    pushLeaves(unmapped);
  }
  return rows;
}

function buildPnlSpec(input: BuildBudgetPackInput, stats: PackStats, gop: GopBands, omittedTotal: number): BudgetPackSheetSpec {
  const { locationName, year, actualMonthKeys, planLabel = 'Budget' } = input;
  const rows: StatementRow[] = [];

  const stat = statBandRows(stats);
  if (stat.length) {
    rows.push(...stat);
    blankRow(rows);
  }

  const defs = comparisonDefs(input.comparison);
  const comp = (rev: ForecastAccount[], exp: ForecastAccount[]): Cells =>
    comparisonCells(rev, exp, input.comparison, defs);
  const deptAccounts = (depts: RawExportDepartment[]): ForecastAccount[] => depts.flatMap((d) => d.accounts || []);
  const allRevenueAccounts = deptAccounts(input.revenueDepartments);
  const allExpenseAccounts = deptAccounts(input.expenseDepartments);

  // Account detail starts collapsed: the department heading hides WITH its
  // accounts (outlineLevel joins them to the group), so the collapsed
  // statement is just the "Total <Dept>" summary rows — expanding shows the
  // heading, the accounts and the total again.
  const pushDept = (isExpense: boolean) => (dept: RawExportDepartment) => {
    rows.push({ style: 'group', label: titleCase(dept.department), indent: 1, cells: null, hidden: true, outlineLevel: 2 });
    dept.accounts.forEach((a) =>
      rows.push({
        style: 'detail',
        label: accountLabel(a),
        indent: 2,
        cells: { ...moneyCells(a.monthlyValues || {}), ...comp(isExpense ? [] : [a], isExpense ? [a] : []) },
        hidden: true,
      })
    );
    rows.push({
      style: 'groupTotal',
      label: `Total ${titleCase(dept.department)}`,
      indent: 1,
      cells: { ...moneyCells(dept.monthlyTotals), ...comp(isExpense ? [] : dept.accounts, isExpense ? dept.accounts : []) },
    });
  };
  const sumDepts = (depts: RawExportDepartment[]): Cells => {
    const rec: Record<string, number> = {};
    MONTHS.forEach((m) => {
      rec[m] = depts.reduce((sum, d) => sum + Number(d.monthlyTotals?.[m] ?? 0), 0);
    });
    return moneyCells(rec);
  };

  rows.push({ style: 'section', label: 'REVENUE', indent: 0, cells: null });
  input.revenueDepartments.forEach(pushDept(false));
  rows.push({
    style: 'sectionTotal', label: 'Total Revenue', indent: 0,
    cells: { ...moneyCells(input.revenueTotals), ...comp(allRevenueAccounts, []) },
  });
  blankRow(rows);

  // Expenses read in USALI statement order: the departments inside GOP
  // (operated, then undistributed), the GOP line, then everything below it.
  rows.push({ style: 'section', label: 'OPERATING EXPENSES', indent: 0, cells: null });
  gop.operatingExpenseDepts.forEach(pushDept(true));
  rows.push({
    style: 'sectionTotal', label: 'Total Operating Expenses', indent: 0,
    cells: { ...sumDepts(gop.operatingExpenseDepts), ...comp([], deptAccounts(gop.operatingExpenseDepts)) },
  });
  blankRow(rows);
  rows.push({
    style: 'net', label: 'GROSS OPERATING PROFIT (GOP)', indent: 0,
    cells: { ...gop.gop, ...comp(allRevenueAccounts, deptAccounts(gop.operatingExpenseDepts)) },
  });
  if (Object.keys(gop.gopMargin).length) rows.push({ style: 'group', label: 'GOP Margin %', indent: 1, cells: gop.gopMargin, format: 'percent1' });
  blankRow(rows);
  if (gop.belowGopExpenseDepts.length > 0) {
    rows.push({ style: 'section', label: 'NON-OPERATING & FIXED CHARGES', indent: 0, cells: null });
    gop.belowGopExpenseDepts.forEach(pushDept(true));
    rows.push({
      style: 'sectionTotal', label: 'Total Non-Operating & Fixed Charges', indent: 0,
      cells: { ...sumDepts(gop.belowGopExpenseDepts), ...comp([], deptAccounts(gop.belowGopExpenseDepts)) },
    });
    blankRow(rows);
  }
  rows.push({
    style: 'sectionTotal', label: 'Total Expenses', indent: 0,
    cells: { ...moneyCells(input.expenseTotals), ...comp([], allExpenseAccounts) },
  });
  blankRow(rows);
  rows.push({
    style: 'net', label: 'NET INCOME', indent: 0,
    cells: { ...moneyCells(input.netIncome), ...comp(allRevenueAccounts, allExpenseAccounts) },
  });

  return {
    sheetName: 'P&L',
    title: locationName,
    subtitle: `Profit & Loss · ${year} ${planLabel}`,
    metaLines: [input.versionName ? `Version: ${input.versionName}` : '', omittedNote(omittedTotal)],
    rowHeaderLabel: 'Account',
    columns: statementColumns(year, actualMonthKeys, planLabel, input.comparison),
    rows,
  };
}

// ─── Department tabs ───

interface DeptSections {
  name: string;
  revenue: RawExportDepartment | null;
  expense: RawExportDepartment | null;
}

/** Revenue-department order first, then expense-only departments. */
function collectDepartments(input: BuildBudgetPackInput): DeptSections[] {
  const byKey = new Map<string, DeptSections>();
  const order: string[] = [];
  input.revenueDepartments.forEach((d) => {
    const key = normalizeDept(d.department);
    byKey.set(key, { name: d.department, revenue: d, expense: null });
    order.push(key);
  });
  input.expenseDepartments.forEach((d) => {
    const key = normalizeDept(d.department);
    const existing = byKey.get(key);
    if (existing) {
      existing.expense = d;
    } else {
      byKey.set(key, { name: d.department, revenue: null, expense: d });
      order.push(key);
    }
  });
  return order.map((k) => byKey.get(k)!).filter(Boolean);
}

const laborMethodLabel = (t: LaborPositionConfig['driverType']): string =>
  ({ min_por: 'Minutes per Occupied Room', hrs_por: 'Hours per Occupied Room', fte: 'FTE (fixed staffing)', pct_revenue: '% of Revenue' }[t] || t);

/** Number format for a driver drill-down row's rate unit. */
const RATE_FORMAT: Record<RateUnit, string> = {
  currency: 'currency2',
  percent: 'percent2',
  minutes: 'number1',
  hours: 'hours',
  fte: 'number2',
  fixed: 'currency',
  wage: 'currency2',
};

/** "Basis: …" line for drivers whose rate multiplies a selectable base. */
function basisNote(a: ForecastAccount, accountName: (id: number) => string): string | null {
  if (isSelectedRevenueDriver(a.driverType) && a.selectedDepartments?.length) {
    return `Basis: revenue of ${a.selectedDepartments.map(titleCase).join(', ')}`;
  }
  if (a.driverType === '% of Account' && a.sourceChartOfAccountIds?.length) {
    return `Basis: ${a.sourceChartOfAccountIds.map(accountName).join(', ')}`;
  }
  return null;
}

function layerBasisNote(layer: ZeroBaseDriverLayer, accountName: (id: number) => string): string | null {
  if (layer.type === 'pct_other' && layer.sourceChartOfAccountIds?.length) {
    return `${layer.description || `Layer ${layer.id}`} basis: ${layer.sourceChartOfAccountIds.map(accountName).join(', ')}`;
  }
  return null;
}

function laborScopeNote(cfg: LaborPositionConfig, accountName: (id: number) => string): string | null {
  if (cfg.driverType !== 'pct_revenue') return null;
  if (cfg.revenueScope === 'department') return `Basis: ${titleCase(cfg.revenueDeptName || '')} department revenue`;
  if (cfg.revenueScope === 'account') {
    const ids = cfg.revenueAccountIds?.length ? cfg.revenueAccountIds : cfg.revenueAccountId != null ? [cfg.revenueAccountId] : [];
    return ids.length ? `Basis: ${ids.map(accountName).join(', ')}` : null;
  }
  return 'Basis: total revenue';
}

/** A sub-row worth printing: some month carries a real rate or an override. */
const subRowHasSignal = (row: DriverSubRow): boolean =>
  MONTHS.some((m) => {
    const c = row.cells[m];
    if (!c) return false;
    return (c.rate !== null && c.rate !== undefined && c.rate !== 0) || c.overridden || !!c.wage?.overridden;
  });

const monthNumberValue = (rec: Record<string | number, number> | undefined, monthNumber: number): number | null => {
  const v = rec?.[monthNumber] ?? rec?.[String(monthNumber)];
  return v === undefined || v === null ? null : Number(v);
};

/** Occupied-Rooms stat leaves linked to this revenue account. */
const leavesLinkedTo = (stats: PackStats, chartOfAccountId: number): StatLeaf[] =>
  (stats.resolved?.occupiedLeaves || []).filter((leaf) => leaf.account.revenueChartOfAccountId === chartOfAccountId);

/** Any keyed room-night month on these leaves = the revenue was planned "By Stat Account". */
const leavesAreKeyed = (leaves: StatLeaf[]): boolean => leaves.some((leaf) => leaf.sources.some((s) => s === 'keyed'));

/**
 * RN + ADR rows per linked stat segment for a rooms-revenue account planned
 * in the revenue modal's "By Stat Account" mode — the segment grain (RACK /
 * ADS / GROUP room nights and their rates) is the budget's real driver.
 */
function statSegmentRows(a: ForecastAccount, leaves: StatLeaf[]): StatementRow[] {
  const rows: StatementRow[] = [];
  leaves.forEach((leaf) => {
    const name = leaf.account.accountName;
    rows.push({ style: 'detail', label: `${name} — room nights`, indent: 2, cells: leafCells(leaf.months), format: 'number' });
    const adrCells: Cells = {};
    MONTHS.forEach((m, i) => {
      const adr = monthNumberValue(leaf.account.adrInputs, i + 1);
      if (adr !== null && adr !== 0) adrCells[m] = round2(adr);
    });
    // Blended ADR for the segment's year: Σ(RN × ADR) ÷ Σ RN over keyed months.
    let revSum = 0;
    let rnSum = 0;
    MONTHS.forEach((m, i) => {
      const adr = adrCells[m];
      const rn = leaf.months[i];
      if (typeof adr === 'number' && rn !== null && rn > 0) {
        revSum += adr * rn;
        rnSum += rn;
      }
    });
    if (rnSum > 0) adrCells.total = round2(revSum / rnSum);
    if (Object.keys(adrCells).length) rows.push({ style: 'detail', label: `${name} — ADR`, indent: 2, cells: adrCells, format: 'currency2' });
  });
  if (leaves.length > 1) {
    const totalRn = MONTHS.map((_, i) => sumMonths(leaves.map((l) => l.months[i])));
    rows.push({ style: 'detail', label: 'Room nights — all segments', indent: 2, cells: leafCells(totalRn), format: 'number' });
    const rev = MONTHS.map((m) => round2(Number(a.monthlyValues?.[m] ?? 0)));
    const blended = ratioCells(rev, totalRn);
    if (Object.keys(blended).length) rows.push({ style: 'detail', label: 'ADR — blended', indent: 2, cells: blended, format: 'currency2' });
  }
  if (leaves.some((leaf) => leaf.sources.some((s) => s === 'prorata'))) {
    rows.push({ style: 'detail', label: 'Months without keyed segments spread room nights pro rata to revenue', indent: 2, cells: null });
  }
  return rows;
}

function driverDetailRows(a: ForecastAccount, currency: string | null | undefined, accountName: (id: number) => string, stats: PackStats): { rows: StatementRow[]; hasOverride: boolean } {
  const rows: StatementRow[] = [];
  let hasOverride = false;
  const note = (label: string) => rows.push({ style: 'detail', label, indent: 2, cells: null });

  const leaves = leavesLinkedTo(stats, a.chartOfAccountId);
  const keyed = leavesAreKeyed(leaves);
  rows.push({
    style: 'group',
    label: `${accountLabel(a)} — ${keyed ? `By Stat Account (RN × ADR) · ${driverLabel(a.driverType)}` : driverLabel(a.driverType)}`,
    indent: 1,
    cells: moneyCells(a.monthlyValues || {}),
  });
  if (leaves.length > 0) rows.push(...statSegmentRows(a, leaves));
  const basis = basisNote(a, accountName);
  if (basis) note(basis);

  const subRows = buildDriverSubRows(a, currency).filter((r) => !r.isEmpty && subRowHasSignal(r));
  const rateCells = (row: DriverSubRow, wage: boolean): Cells => {
    const cells: Cells = {};
    MONTHS.forEach((m) => {
      const c = row.cells[m];
      const src = wage ? c?.wage : c;
      if (!src || src.rate === null || src.rate === undefined || !Number.isFinite(src.rate)) return;
      if (src.overridden) {
        hasOverride = true;
        // Overridden months carry the formatted rate as text so the " *"
        // marker survives; un-overridden months stay numeric.
        cells[m] = `${src.text} *`;
      } else {
        cells[m] = src.rate;
      }
    });
    return cells;
  };

  subRows.forEach((row) => {
    rows.push({
      style: 'detail',
      label: `${row.label} — ${row.sub || row.badge}`,
      indent: 2,
      cells: rateCells(row, false),
      format: RATE_FORMAT[row.unit] || 'number2',
    });
    if (row.hasWage) {
      rows.push({
        style: 'detail',
        label: `${row.label} — hourly rate`,
        indent: 2,
        cells: rateCells(row, true),
        format: RATE_FORMAT.wage,
      });
    }
  });

  if (a.driverType === 'Labor Config' && a.laborPositionConfigs?.length) {
    a.laborPositionConfigs.forEach((cfg) => {
      const config = [
        laborMethodLabel(cfg.driverType),
        cfg.value != null ? `value ${cfg.value}` : null,
        cfg.hourlyRate != null ? `rate ${cfg.hourlyRate}/hr` : 'rate auto (payroll)',
      ].filter(Boolean).join(' · ');
      note(`${cfg.positionName} config — ${config}`);
      const scope = laborScopeNote(cfg, accountName);
      if (scope) note(`${cfg.positionName} ${scope[0].toLowerCase()}${scope.slice(1)}`);
    });
  }
  if (a.driverType === 'Zero Base' && a.zeroBaseDriverLayers?.length) {
    a.zeroBaseDriverLayers.forEach((layer) => {
      const layerNote = layerBasisNote(layer, accountName);
      if (layerNote) note(layerNote);
    });
  }
  return { rows, hasOverride };
}

function buildDeptSpec(dept: DeptSections, sheetName: string, input: BuildBudgetPackInput, stats: PackStats, omitted: number): BudgetPackSheetSpec {
  const { year, actualMonthKeys, planLabel = 'Budget', currency } = input;
  const rows: StatementRow[] = [];

  const accountName = (() => {
    const map = new Map<number, string>();
    [...input.revenueDepartments, ...input.expenseDepartments].forEach((d) =>
      d.accounts.forEach((a) => map.set(a.chartOfAccountId, `${a.accountNumber} ${a.accountName}`.trim()))
    );
    return (id: number) => map.get(id) || `#${id}`;
  })();

  const defs = comparisonDefs(input.comparison);
  const comp = (rev: ForecastAccount[], exp: ForecastAccount[]): Cells =>
    comparisonCells(rev, exp, input.comparison, defs);
  const revAccounts = dept.revenue?.accounts || [];
  const expAccounts = dept.expense?.accounts || [];
  const revCells = dept.revenue ? { ...moneyCells(dept.revenue.monthlyTotals), ...comp(revAccounts, []) } : null;
  const expCells = dept.expense ? { ...moneyCells(dept.expense.monthlyTotals), ...comp([], expAccounts) } : null;

  rows.push({ style: 'section', label: 'DEPARTMENT SUMMARY', indent: 0, cells: null });
  // The rooms department carries the property's room-night KPIs alongside its
  // dollars — Occ %, ADR and RevPAR are how an owner reads this department.
  // "Rooms" is recognized by its stat-linked revenue accounts, not its name.
  const isRoomsDept =
    normalizeDept(dept.name) === 'rooms' ||
    (dept.revenue?.accounts || []).some((a) => stats.linkedRevenueIds.has(a.chartOfAccountId));
  if (isRoomsDept) rows.push(...kpiRows(stats, 1));
  if (revCells) rows.push({ style: 'groupTotal', label: 'Revenue', indent: 1, cells: revCells });
  if (expCells) rows.push({ style: 'groupTotal', label: 'Expenses', indent: 1, cells: expCells });
  if (revCells && expCells) {
    const net: Cells = {};
    [...MONTHS, 'total'].forEach((k) => {
      net[k] = round2(Number(revCells[k] ?? 0) - Number(expCells[k] ?? 0));
    });
    rows.push({ style: 'net', label: 'NET', indent: 0, cells: { ...net, ...comp(revAccounts, expAccounts) } });
  }
  blankRow(rows);

  const pushAccounts = (heading: string, totalLabel: string, section: RawExportDepartment, totals: Cells, isExpense: boolean) => {
    rows.push({ style: 'section', label: heading, indent: 0, cells: null });
    section.accounts.forEach((a) =>
      rows.push({
        style: 'detail',
        label: accountLabel(a),
        indent: 1,
        cells: { ...moneyCells(a.monthlyValues || {}), ...comp(isExpense ? [] : [a], isExpense ? [a] : []) },
      })
    );
    rows.push({ style: 'sectionTotal', label: totalLabel, indent: 0, cells: totals });
  };
  if (dept.revenue) {
    pushAccounts('REVENUE', 'Total Revenue', dept.revenue, revCells!, false);
    blankRow(rows);
  }
  if (dept.expense) {
    pushAccounts('EXPENSES', 'Total Expenses', dept.expense, expCells!, true);
    blankRow(rows);
  }

  // Driver detail leads with the accounts a driver actually moves; manual and
  // static entries collapse into one group at the end — their dollars are
  // already in the sections above.
  // Every account in the sections above gets a Driver Detail entry, in the
  // same order — manually keyed accounts say so on their line, so nothing
  // reads as missing.
  rows.push({ style: 'section', label: 'DRIVER DETAIL', indent: 0, cells: null });
  const accounts = [...(dept.revenue?.accounts || []), ...(dept.expense?.accounts || [])];
  let hasOverride = false;
  accounts.forEach((a) => {
    const detail = driverDetailRows(a, currency, accountName, stats);
    rows.push(...detail.rows);
    if (detail.hasOverride) hasOverride = true;
  });
  if (hasOverride) {
    blankRow(rows);
    rows.push({ style: 'detail', label: '* driver-level monthly override (driver stays live)', indent: 0, cells: null });
  }

  return {
    sheetName,
    title: titleCase(dept.name),
    subtitle: `${input.locationName} · ${year} ${planLabel}`,
    metaLines: [input.versionName ? `Version: ${input.versionName}` : '', omittedNote(omitted)],
    rowHeaderLabel: 'Account / Assumption',
    columns: statementColumns(year, actualMonthKeys, planLabel, input.comparison),
    rows,
  };
}

// ─── Workbook ───

export function buildBudgetPackSheetSpecs(rawInput: BuildBudgetPackInput): BudgetPackSheetSpec[] {
  const { input, omittedByDept, omittedTotal } = suppressZeroAccounts(rawInput);
  const stats = computeStats(input);
  const gop = classifyGop(input);
  const taken = new Set<string>();
  const overviewName = sanitizeSheetName('Overview', taken);
  const pnlName = sanitizeSheetName('P&L', taken);
  const departments = collectDepartments(input).map((dept) => ({
    dept,
    sheetName: sanitizeSheetName(titleCase(dept.name), taken),
  }));

  const overview = { ...buildOverviewSpec(input, stats, gop, departments, pnlName, omittedTotal), sheetName: overviewName };
  const pnl = { ...buildPnlSpec(input, stats, gop, omittedTotal), sheetName: pnlName };
  const deptSpecs = departments.map(({ dept, sheetName }) =>
    buildDeptSpec(dept, sheetName, input, stats, omittedByDept.get(normalizeDept(dept.name)) || 0)
  );
  return [overview, pnl, ...deptSpecs];
}

// ─── Export tab ───

export const EXPORT_SHEET_NAME = 'Export';

/**
 * The flat dump behind the "Export" tab: one row per account — EVERY money
 * account in the version (zero rows included) and every stat account the
 * Statistics band resolves (room-night segments, other mapped stats, and the
 * Other Statistics plan) — with one plain-number column per month. Header on
 * row 1, no titles, no merged cells, no formatting: the shape an accounting
 * system's budget import wants. Revenue keeps its on-screen sign; expense rows
 * are negated (a positive on-screen expense exports negative, a contra-expense
 * credit exports positive). Statistics keep their natural sign.
 */
export function buildExportSheetRows(input: BuildBudgetPackInput): CellValue[][] {
  const { locationName, year } = input;
  const header: CellValue[] = [
    'Location', 'Account Number', 'Account Name', 'Department', 'Account Type', 'Section',
    ...MONTH_NAMES.map((m) => `${m} ${year}`),
    'Total',
  ];
  const rows: CellValue[][] = [header];
  const num = (v: number | null | undefined) => round2(Number(v ?? 0) || 0);

  const pushMoney = (dept: RawExportDepartment, section: 'Revenue' | 'Expense') => {
    const sign = section === 'Expense' ? -1 : 1;
    dept.accounts.forEach((a) => {
      // `|| 0` keeps a negated zero from exporting as -0.
      const months = MONTHS.map((m) => sign * num(a.monthlyValues?.[m]) || 0);
      rows.push([
        locationName, a.accountNumber || '', a.accountName || '', dept.department || a.department || '',
        a.accountType || section, section, ...months, round2(months.reduce((s, v) => s + v, 0)),
      ]);
    });
  };
  input.revenueDepartments.forEach((d) => pushMoney(d, 'Revenue'));
  input.expenseDepartments.forEach((d) => pushMoney(d, 'Expense'));

  // Every P&L account on the chart that the plan doesn't carry (no
  // department, never forecast, brand new) still gets a row of zeros — a
  // missing account reads as "dropped", a zero row reads as "nothing planned".
  const emitted = new Set<number>();
  [...input.revenueDepartments, ...input.expenseDepartments].forEach((d) => d.accounts.forEach((a) => emitted.add(a.chartOfAccountId)));
  const revenueTypes = new Set(['revenue', 'other income']);
  const expenseTypes = new Set(['expense', 'other expense', 'cost of goods sold']);
  const zeros = MONTHS.map(() => 0);
  (input.chartOfAccounts || [])
    .filter((a) => !emitted.has(a.id))
    .filter((a) => { const t = String(a.accountType || '').trim().toLowerCase(); return revenueTypes.has(t) || expenseTypes.has(t); })
    .sort((a, b) => String(a.accountNumber || '').localeCompare(String(b.accountNumber || ''), undefined, { numeric: true }))
    .forEach((a) => {
      const section = revenueTypes.has(String(a.accountType || '').trim().toLowerCase()) ? 'Revenue' : 'Expense';
      rows.push([locationName, a.accountNumber || '', a.accountName || '', a.department || '', a.accountType || section, section, ...zeros, 0]);
    });

  const hasStats = !!input.roomStats || (input.statAccounts?.length ?? 0) > 0;
  if (hasStats) {
    const resolved = resolveStatAccountMonths(input);
    const byNumber = (a: StatLeaf, b: StatLeaf) =>
      a.account.accountNumber.localeCompare(b.account.accountNumber, undefined, { numeric: true }) || a.account.accountName.localeCompare(b.account.accountName);
    const pushLeaves = (leaves: StatLeaf[], section: string) => {
      [...leaves].sort(byNumber).forEach((leaf) => {
        const months = leaf.months.map((v) => num(v));
        rows.push([
          locationName, leaf.account.accountNumber || '', leaf.account.accountName || '', leaf.account.department || '',
          'Statistics', section, ...months, round2(months.reduce((s, v) => s + v, 0)),
        ]);
      });
    };
    pushLeaves(resolved.occupiedLeaves, 'Statistics · Occupied Rooms');
    pushLeaves(resolved.availableLeaves, 'Statistics · Available Rooms');
    Array.from(resolved.otherMapped.keys()).sort((a, b) => a.localeCompare(b)).forEach((type) => pushLeaves(resolved.otherMapped.get(type)!, `Statistics · ${type}`));
    pushLeaves(resolved.unmapped, 'Statistics · Other');
  }
  return rows;
}

/** The Export tab as a SheetJS worksheet: header row 1, numbers as numbers. */
export function buildExportSheet(input: BuildBudgetPackInput): XLSX.WorkSheet {
  const aoa = buildExportSheetRows(input);
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const textCols = 6;
  ws['!cols'] = [
    { wch: 28 }, { wch: 16 }, { wch: 40 }, { wch: 22 }, { wch: 14 }, { wch: 28 },
    ...MONTH_NAMES.map(() => ({ wch: 12 })), { wch: 14 },
  ];
  // Plain number format on every value cell so nothing imports as text.
  for (let r = 1; r < aoa.length; r++) {
    for (let c = textCols; c < aoa[r].length; c++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (cell && cell.t === 'n') cell.z = '0.00';
    }
  }
  ws['!freeze'] = { xSplit: 3, ySplit: 1 };
  return ws;
}

/**
 * Build the styled multi-tab workbook and download it. `exportFormat` — the
 * organization's (or location's) saved layout for the Export tab; null keeps
 * the built-in layout.
 */
export async function downloadBudgetPackXlsx(input: BuildBudgetPackInput, filename: string, exportFormat: BudgetExportFormat | null = null): Promise<void> {
  const specs = buildBudgetPackSheetSpecs(input);
  const wb = XLSX.utils.book_new();
  specs.forEach((spec) => {
    XLSX.utils.book_append_sheet(wb, buildStatementSheet(spec), spec.sheetName);
  });
  // Last tab: the flat, import-ready dump of every account by month.
  const exportName = sanitizeSheetName(exportFormat?.sheetName || EXPORT_SHEET_NAME, new Set(specs.map((s) => s.sheetName)));
  const exportSheet = exportFormat ? buildFormattedExportSheet(input, exportFormat) : buildExportSheet(input);
  XLSX.utils.book_append_sheet(wb, exportSheet, exportName);
  await downloadWorkbookXlsx(wb, filename);
}
